/** Two colours that a gradient runs between. Both are dark enough for white text (at least 4.5:1). */
export interface Palette {
    name: string;
    from: string;
    to: string;
}

export const PALETTES: readonly Palette[] = [
    { name: "teal-blue", from: "#0f766e", to: "#1e3a8a" },
    { name: "pink-violet", from: "#be185d", to: "#5b21b6" },
    { name: "indigo-teal", from: "#3730a3", to: "#0f766e" },
    { name: "rust-wine", from: "#b45309", to: "#7f1d1d" },
    { name: "emerald-slate", from: "#047857", to: "#1e293b" },
    { name: "violet-rose", from: "#6d28d9", to: "#be123c" },
    { name: "blue-cyan", from: "#1d4ed8", to: "#0e7490" },
    { name: "slate-plum", from: "#334155", to: "#581c87" },
    { name: "forest-olive", from: "#166534", to: "#854d0e" },
    { name: "crimson-navy", from: "#9f1239", to: "#1e3a8a" },
    { name: "ocean", from: "#075985", to: "#134e4a" },
    { name: "berry", from: "#86198f", to: "#9d174d" },
];

export const ANGLES: readonly number[] = [120, 135, 150, 160, 200];

/** FNV-1a: the same text always gives the same number, and neighbouring texts give unrelated ones. */
const hash = (text: string): number => {
    let h = 0x811c9dc5;
    for (const ch of text) {
        h ^= ch.codePointAt(0) ?? 0;
        h = Math.imul(h, 0x01000193);
    }
    return h >>> 0;
};

/** The palette and angle a seed (a slug) always gets, so a tile keeps its look from visit to visit. */
export const gradientParts = (seed: string): { palette: Palette; angle: number } => {
    const h = hash(seed);
    return { palette: PALETTES[h % PALETTES.length], angle: ANGLES[(h >>> 8) % ANGLES.length] };
};

/** A CSS background for a tile with no picture: a soft light in one corner over a gradient between two deep colours. */
export const gradientFor = (seed: string): string => {
    const { palette, angle } = gradientParts(seed);
    return `radial-gradient(120% 90% at 88% 0%, rgb(255 255 255 / 20%), transparent 60%), linear-gradient(${angle}deg, ${palette.from}, ${palette.to})`;
};
